import {z} from 'zod';
import {congestionAreaClusterIdSchema, congestionAreaIdSchema} from './id';
import type {CongestionAreaClusterId, CongestionAreaId} from './id';
import type {SolvieProfile} from './request-contract';

/**
 * A guard is typed in MW because that is the unit ElaadNL states it in (SM013, open
 * question §20); every curve in this system is in kW. The conversion happens once, here,
 * so no comparison downstream has to remember which side it is on.
 */
const KW_PER_MW = 1000;

/**
 * How far a quarter may sit above the guard and still be left alone: ten watts, the
 * resolution power is expressed at (§5.3 rule 15 allows two decimals of a kW).
 *
 * Without it, summing a dozen doubles that happen to land exactly on the guard produces
 * an excess of `1e-11` kW, and the reader is shown a cut of nothing at all on a quarter
 * that fits. The cost is that the guard may be exceeded by under a hundredth of a kW —
 * five parts in ten million of a 20 MW guard, and below what the wire can express.
 */
const CUT_THRESHOLD_KW = 0.01;

/**
 * Cuts are rounded **down** to the two decimals a power may carry, never to nearest.
 * Rounding to nearest lets a scaled quarter land above its share, and a dozen curves each
 * rounded up puts the total back over the guard it was just cut to fit — a guard that
 * reads as enforced and is not.
 */
const floorPower = (value: number): number => Math.floor(value * 100) / 100;

/**
 * A cluster of congestion areas as the guard needs it: its identity, so a screen can name
 * what bound the request, and its ceiling.
 *
 * `guardMw` is nullable and a null one binds nothing. A cluster without a guard still
 * travels, because "this area is in G1B and G1B has no ceiling" is a different thing to
 * show than "this area is in no cluster".
 */
export const guardClusterSchema = z.object({
  id: congestionAreaClusterIdSchema,
  code: z.string().nullable(),
  name: z.string(),
  guardMw: z.number().nonnegative().nullable(),
});

export type GuardCluster = z.infer<typeof guardClusterSchema>;

/**
 * The ceiling a request was measured against, read from the cluster its guard record froze.
 *
 * Null unless that record names the cluster the area is in now: a ceiling set, changed or
 * cleared after the request was made says nothing about it, and neither does a cluster the
 * area joined later. Only `id` and `guardMw` are read, so a record frozen by an older
 * version still answers.
 */
export const frozenCeilingOf = (clusterId: CongestionAreaClusterId, frozen: unknown): number | null => {
  const parsed = guardClusterSchema.pick({id: true, guardMw: true}).safeParse(frozen);
  return parsed.success && parsed.data.id === clusterId ? parsed.data.guardMw : null;
};

/**
 * Which ceiling moved a quarter: the area's own, the cluster's, or both in turn (ADR 0038).
 * Two ceilings over one dispatch are only readable if every cut says which one it was.
 */
export const GUARD_SOURCES = ['AREA', 'CLUSTER'] as const;

export const guardSourceSchema = z.enum(GUARD_SOURCES);

export type GuardSource = z.infer<typeof guardSourceSchema>;

/** One quarter the guard moved, and by how much. Quarters that fit are not recorded. */
export const guardCutQuarterSchema = z.object({
  /** The instant the quarter begins, as every curve in the profile is keyed. */
  quarter: z.coerce.date(),
  /** What Solvie asked for across this area's resource groups, in kW, before the guard. */
  requestedKw: z.number().nonnegative(),
  /** What is left for it after the guard, in kW. */
  allowedKw: z.number().nonnegative(),
  /**
   * Which ceilings cut this quarter, area before cluster. A record frozen before an area
   * could have a ceiling carries none, and every cut in it was the cluster's.
   */
  boundBy: z.array(guardSourceSchema).default(['CLUSTER']),
});

export type GuardCutQuarter = z.infer<typeof guardCutQuarterSchema>;

/**
 * What the guard did to one area's profile, frozen onto the request beside the profile
 * itself so the detail screen can say the dispatch is smaller than the solver's answer
 * and why — which is the whole of what a reader is owed here.
 *
 * Recorded even when nothing was cut: "G1B, 30 MW, fits" is worth showing, and a null
 * record would make a guard that held indistinguishable from no guard at all.
 */
export const guardApplicationSchema = z.object({
  /** The area's own ceiling in MW, or null for an area with none. Absent before ADR 0038. */
  areaGuardMw: z.number().nonnegative().nullable().default(null),
  /** The cluster whose ceiling covered this area, or null for an area in none. */
  cluster: guardClusterSchema.nullable(),
  /**
   * kW already held for this cluster at each quarter by requests outside this one, at the
   * moment the profile was calculated. Only the quarters this area also asks for, and
   * only where it is above zero — it is evidence for the cut, not a second curve.
   */
  committedKw: z.array(z.object({quarter: z.coerce.date(), kw: z.number().nonnegative()})),
  /** Every quarter that was cut, in time order. Empty when the profile fitted as it was. */
  cuts: z.array(guardCutQuarterSchema),
});

export type GuardApplication = z.infer<typeof guardApplicationSchema>;

/** One area entering the guard: its profile and the two ceilings that may bind it. */
export interface GuardedArea {
  congestionAreaId: CongestionAreaId;
  /** The area's own ceiling in MW, or null for none. */
  guardMw: number | null;
  cluster: GuardCluster | null;
  profile: SolvieProfile;
}

export const guardedResultSchema = z.object({
  congestionAreaId: congestionAreaIdSchema,
  /** The profile as it will be dispatched — the input unchanged when nothing was cut. */
  profile: z.custom<SolvieProfile>(),
  guard: guardApplicationSchema.nullable(),
});

export type GuardedResult = z.infer<typeof guardedResultSchema>;

/**
 * What areas *outside* this calculation already hold, per cluster and per quarter, in kW.
 * Keyed by cluster id and then by the epoch milliseconds a quarter begins — a `Date` is not
 * a map key, and every curve here is keyed by the instant rather than by an index (§27).
 */
export type CommittedKwByCluster = ReadonlyMap<string, ReadonlyMap<number, number>>;

/** The area's requested power per quarter: the sum over its resource-group curves. */
const areaTotals = (profile: SolvieProfile): Map<number, number> => {
  const totals = new Map<number, number>();
  for (const curve of profile.requested_flex_profiles) {
    for (const interval of curve.requested_flex) {
      const key = interval.quarter.getTime();
      totals.set(key, (totals.get(key) ?? 0) + interval.flex_power);
    }
  }
  return totals;
};

/**
 * The factors to apply to one area, per quarter. Absent means "leave this quarter alone",
 * which is not the same as a factor of 1: only a quarter that is actually scaled is
 * rewritten, so an untouched quarter keeps the precision Solvie sent.
 */
type Factors = Map<number, number>;

/**
 * How much of a quarter survives, or `undefined` for a quarter that is left alone.
 *
 * `allowed` is never negative — the caller clamps it — so passing the first test means
 * `total` is above it and therefore positive; no separate zero check is reachable.
 */
const scaleFactor = (total: number, allowed: number): number | undefined => {
  if (total <= allowed + CUT_THRESHOLD_KW) return undefined;
  return allowed <= 0 ? 0 : allowed / total;
};

/**
 * The profile with the given factors applied.
 *
 * **`total_requested_flex` is recomputed at a scaled quarter rather than scaled itself.**
 * It is the sum over the flex groups and is shown but never published (`request-contract.ts`),
 * while the per-group curves are what an event actually carries. Scaling both
 * independently would leave the number the reader sees a hundredth of a kW away from the
 * number the grid gets, for no gain; deriving it keeps the two the same thing.
 */
const scaled = (profile: SolvieProfile, factors: Factors): {profile: SolvieProfile; cutTotals: Map<number, number>} => {
  const cutTotals = new Map<number, number>();
  if (factors.size === 0) return {profile, cutTotals};

  const requested_flex_profiles = profile.requested_flex_profiles.map(curve => ({
    ...curve,
    requested_flex: curve.requested_flex.map(interval => {
      const factor = factors.get(interval.quarter.getTime());
      if (factor === undefined) return interval;

      const flex_power = floorPower(interval.flex_power * factor);
      const key = interval.quarter.getTime();
      cutTotals.set(key, (cutTotals.get(key) ?? 0) + flex_power);
      return {...interval, flex_power};
    }),
  }));

  const total_requested_flex = profile.total_requested_flex.map(interval => {
    const total = cutTotals.get(interval.quarter.getTime());
    return total === undefined ? interval : {...interval, flex_power: total};
  });

  return {profile: {...profile, requested_flex_profiles, total_requested_flex}, cutTotals};
};

/**
 * Applies the MW guards to the profiles of one preview, and reports what they did.
 *
 * **Two ceilings, in this order** (ADR 0038). An area's own ceiling caps that area alone,
 * per quarter-hour. The cluster's ceiling then caps the sum over every area of the cluster
 * this preview covers, judged against what each area asks for *after* its own cut — so a
 * cluster's headroom is never spent on power an area ceiling had already taken away. An
 * area under neither is handed back exactly as the solver answered.
 *
 * **What is already committed comes off the cluster first.** `committedKw` is what areas in
 * the cluster hold for this day through requests outside this preview; the previewed areas
 * share whatever is left. A cluster already at its ceiling leaves no headroom, and every
 * quarter is cut to zero — which the caller sees as nothing to dispatch.
 *
 * **Cuts are pro rata.** One factor per quarter, applied to every contributing curve, so
 * each area and each resource group keeps its share of an answer the solver balanced.
 * Allocating in some order instead would be a policy about which area matters more, and
 * nobody has stated one.
 */
export const applyMwGuards = (areas: GuardedArea[], committedKw: CommittedKwByCluster): GuardedResult[] => {
  /**
   * One record per area rather than maps keyed by its id. The maps were always populated
   * for every area, so every `get` needed a fallback that could not happen.
   */
  const working = areas.map(area => ({
    area,
    before: areaTotals(area.profile),
    factors: new Map<number, number>(),
    bound: new Map<number, Set<GuardSource>>(),
  }));
  type Entry = (typeof working)[number];

  /** The area's total at a quarter with the factors settled so far already applied. */
  const runningTotal = (entry: Entry, quarter: number): number =>
    (entry.before.get(quarter) ?? 0) * (entry.factors.get(quarter) ?? 1);

  const cut = (entry: Entry, quarter: number, factor: number, source: GuardSource): void => {
    entry.factors.set(quarter, (entry.factors.get(quarter) ?? 1) * factor);
    const sources = entry.bound.get(quarter) ?? new Set<GuardSource>();
    sources.add(source);
    entry.bound.set(quarter, sources);
  };

  for (const entry of working) {
    if (entry.area.guardMw === null) continue;
    const allowed = entry.area.guardMw * KW_PER_MW;
    for (const [quarter, total] of entry.before) {
      const factor = scaleFactor(total, allowed);
      if (factor !== undefined) cut(entry, quarter, factor, 'AREA');
    }
  }

  const byCluster = new Map<string, {guardMw: number; members: Entry[]}>();
  for (const entry of working) {
    const cluster = entry.area.cluster;
    if (cluster === null || cluster.guardMw === null) continue;
    const existing = byCluster.get(cluster.id);
    if (existing) existing.members.push(entry);
    else byCluster.set(cluster.id, {guardMw: cluster.guardMw, members: [entry]});
  }

  for (const [clusterId, {guardMw, members}] of byCluster) {
    const allowed = guardMw * KW_PER_MW;
    const committed = committedKw.get(clusterId);

    // The union, not one member's keys: two areas of a cluster may be asked about
    // different hours, and a quarter only one of them wants is still the cluster's.
    const quarters = new Set(members.flatMap(member => [...member.before.keys()]));

    for (const quarter of quarters) {
      const held = committed?.get(quarter) ?? 0;
      const previewed = members.reduce((sum, member) => sum + runningTotal(member, quarter), 0);

      // Never below zero: a cluster already over its ceiling leaves no room rather than
      // owing some, and a negative headroom would scale the curves the wrong way.
      const headroom = Math.max(0, allowed - held);
      const factor = scaleFactor(previewed, headroom);
      if (factor === undefined) continue;

      for (const member of members) {
        if (member.before.has(quarter)) cut(member, quarter, factor, 'CLUSTER');
      }
    }
  }

  return working.map(({area, before, factors, bound}) => {
    const {profile, cutTotals} = scaled(area.profile, factors);

    if (area.cluster === null && area.guardMw === null) {
      return {congestionAreaId: area.congestionAreaId, profile, guard: null};
    }

    const cuts = [...before]
      .filter(([quarter]) => factors.has(quarter))
      .sort(([left], [right]) => left - right)
      .map(([quarter, requestedKw]) => ({
        quarter: new Date(quarter),
        requestedKw,
        // Every scaled quarter is one `scaled` just summed, so this is always a hit.
        allowedKw: cutTotals.get(quarter) ?? 0,
        boundBy: GUARD_SOURCES.filter(source => bound.get(quarter)?.has(source)),
      }));

    const clusterCommitted =
      area.cluster === null
        ? new Map<number, number>()
        : (committedKw.get(area.cluster.id) ?? new Map<number, number>());

    return {
      congestionAreaId: area.congestionAreaId,
      profile,
      guard: {
        areaGuardMw: area.guardMw,
        cluster: area.cluster,
        committedKw: [...clusterCommitted]
          .filter(([quarter, kw]) => kw > 0 && before.has(quarter))
          .sort(([left], [right]) => left - right)
          .map(([quarter, kw]) => ({quarter: new Date(quarter), kw})),
        cuts,
      },
    };
  });
};

/** One request already holding flexibility for a cluster on the day being previewed. */
export interface CommittedProfile {
  clusterId: string;
  /** The profile as it was frozen — already cut by whatever guard applied to it then. */
  profile: SolvieProfile;
}

/**
 * What every request outside this preview already holds, per cluster and per quarter.
 *
 * Summed from the **stored** profiles, which are the cut ones: a request that was itself
 * trimmed to fit holds what it will dispatch, not what its solver first asked for.
 * Counting the uncut answer would refuse headroom that was never taken.
 */
export const committedKwByClusterOf = (committed: CommittedProfile[]): CommittedKwByCluster => {
  const byCluster = new Map<string, Map<number, number>>();

  for (const {clusterId, profile} of committed) {
    const quarters = byCluster.get(clusterId) ?? new Map<number, number>();
    for (const [quarter, kw] of areaTotals(profile)) {
      quarters.set(quarter, (quarters.get(quarter) ?? 0) + kw);
    }
    byCluster.set(clusterId, quarters);
  }

  return byCluster;
};
